import { Pin } from '@/components/Icons/Pin';
import PremiumStar from '@/components/Icons/PremiumStar';
import { assetGroupIsLut } from '@/features/Downloads/Gateway';
import { getImageData } from '@/components/Modals/ConfirmDownload/utils/getImageData';
import { FADE_AWAY_BG_COLORS } from '@/constants/drawer';
import { useRouterEvents } from '@/hooks/navigation/useRouterEvents';
import { cn } from '@/lib/utils';
import type { GetBoardsItemResponse } from '@/open-api/model';
import { logEvent } from '@/pages/api/v1/utils';
import type { AssetGroup } from '@/types/asset';
import { Plus } from '@music-vine/cadence/icons';
import { Button, Checkbox, Label, ScrollArea, Separator, Skeleton } from '@music-vine/cadence/ui';
import { cva } from 'class-variance-authority';
import { AnimatePresence, motion } from 'motion/react';
import Image from 'next/image';
import type { Checkbox as CheckboxType } from 'radix-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CloseButton } from './close-button';
import { BOARDS_POPUP_CLOSE_DELAY_MS } from './constants';
import { useBoardsContext } from './context-provider';
import { DialogCreateBoard } from './dialog-create-board';
import { CreateBoardDrawer } from './drawer-create-board';
import type { TimebarAnimationVariant } from './models';
import { Timebar } from './timebar';
import { boardAssetTypeToAssetType, getBoardAssetTypeFromAssetGroup } from './utils/get-board-asset-type-from-asset-group';
const HeaderCloseButton = ({
  className
}: {
  className?: string;
}) => {
  const {
    closeBoardsPopup
  } = useBoardsContext();
  return <CloseButton className={className} onClick={() => closeBoardsPopup()} data-sentry-element="CloseButton" data-sentry-component="HeaderCloseButton" data-sentry-source-file="popup.tsx" />;
};
const MUSIC_ASSET_IMAGE_WIDTH_DEFAULT = 56 as const;
const ASSET_IMAGE_HEIGHT_DEFAULT = 56 as const;
const MOTION_ASSET_IMAGE_WIDTH_DEFAULT = 94.5 as const;
const MOTION_ASSET_W_TO_H_RATIO = MOTION_ASSET_IMAGE_WIDTH_DEFAULT / ASSET_IMAGE_HEIGHT_DEFAULT;
export const AssetImage = ({
  assetType,
  imgSrc,
  isPremium = false,
  imageHeight
}: {
  assetType: AssetGroup['type'];
  imgSrc: string;
  isPremium?: boolean;
  imageHeight?: number;
}) => {
  if (assetType === 'music' || assetType === 'sfx') {
    const width = imageHeight ?? MUSIC_ASSET_IMAGE_WIDTH_DEFAULT;
    const height = imageHeight ?? ASSET_IMAGE_HEIGHT_DEFAULT;
    return <div className="relative" style={{
      width,
      height,
      minWidth: width,
      minHeight: height
    }}>
        <Image alt={'asset cover image'} className={cn('rounded-full')} height={height} src={imgSrc} width={width} />
        {!!isPremium && <PremiumStar className="absolute top-0 left-0 size-5" showBackground />}
      </div>;
  }
  const height = imageHeight ?? ASSET_IMAGE_HEIGHT_DEFAULT;
  const width = imageHeight ? imageHeight * MOTION_ASSET_W_TO_H_RATIO : MOTION_ASSET_IMAGE_WIDTH_DEFAULT;
  return <div className="relative flex" style={{
    width,
    height
  }} data-sentry-component="AssetImage" data-sentry-source-file="popup.tsx">
      <Image alt={'asset cover image'} className={cn('rounded-lg object-cover')} height={height} src={imgSrc} width={width} data-sentry-element="Image" data-sentry-source-file="popup.tsx" />
      {!!isPremium && <PremiumStar className="absolute -top-1.5 -left-1.5 size-5" showBackground />}
    </div>;
};
const headerMessageMap = {
  music: 'Track added!',
  sfx: 'Sound effect added!',
  motion: 'Motion asset added!',
  stock_footage: 'Stock video added!',
  lut: 'LUT added!',
  default: 'Added!'
};
const HeaderContainerVariants = cva('h-24 w-full', {
  variants: {
    inDrawer: {
      false: 'px-4 pt-4',
      true: 'px-6 pt-0'
    }
  },
  defaultVariants: {
    inDrawer: false
  }
});
export const Header = ({
  inDrawer
}: {
  inDrawer?: boolean;
}) => {
  const {
    asset
  } = useBoardsContext();
  const headerAssetData = useMemo(() => {
    if (!asset) {
      return null;
    }
    const {
      imgSrc
    } = getImageData(asset);
    let message = headerMessageMap[asset.type] ?? headerMessageMap.default;
    const assetName = asset.data.name;
    if (asset.type === 'motion' && assetGroupIsLut({
      asset
    })) {
      message = headerMessageMap.lut;
    }
    return {
      imgSrc,
      message,
      assetName
    };
  }, [asset]);
  if (!(asset && headerAssetData)) {
    return <div className="h-24 w-full px-4 pt-4">
        <div className="flex flex-col gap-4">
          <div className="flex w-full items-center gap-2">
            <div className={cn('size-11 bg-card-bg')}>
              <Skeleton className="h-11 w-11 rounded-full" />
            </div>
            <div className="flex h-full w-full flex-col gap-2">
              <Skeleton className="h-6 w-32 rounded-md" />
              <Skeleton className="h-3 w-12 rounded" />
            </div>
            <div className="relative flex h-full w-full shrink justify-end self-start">
              <HeaderCloseButton className={'absolute -top-2 -right-2'} />
            </div>
          </div>
          <Separator className="dark:bg-gray-800" />
        </div>
      </div>;
  }
  const {
    imgSrc: headerAssetImageSrc,
    message: headerMessage,
    assetName: headerAssetName
  } = headerAssetData;
  return <div className={cn(HeaderContainerVariants({
    inDrawer
  }))} data-sentry-component="Header" data-sentry-source-file="popup.tsx">
      <div className="flex flex-col gap-4">
        <div className={cn('flex w-full items-center justify-start gap-3')}>
          <AssetImage assetType={asset.type} imgSrc={headerAssetImageSrc} isPremium={asset.data.is_premium} data-sentry-element="AssetImage" data-sentry-source-file="popup.tsx" />
          <div className="flex w-full flex-col gap-0.5 text-left">
            <div className="font-sans font-semibold text-base text-black tracking-tight dark:text-white">
              {headerMessage}
            </div>
            <div className="line-clamp-1 font-sans text-gray-700 text-sm dark:text-gray-200">
              {headerAssetName}
            </div>
          </div>
          {!inDrawer && <div className="relative flex justify-end self-start">
              <HeaderCloseButton className={'absolute -top-2 -right-2'} />
            </div>}
        </div>
        <Separator className="dark:bg-gray-800" data-sentry-element="Separator" data-sentry-source-file="popup.tsx" />
      </div>
    </div>;
};
export const Footer = ({
  onClickDone
}: {
  onClickDone?: () => void;
}) => {
  const {
    closeBoardsPopup,
    asset
  } = useBoardsContext();
  const handleOnClickDone = useCallback(() => {
    const boardAssetType = asset ? getBoardAssetTypeFromAssetGroup(asset) : undefined;
    void logEvent({
      event_type: 'click',
      asPath: typeof window === 'undefined' ? '' : window.location.pathname,
      metadata: {
        type: 'boards_done_clicked',
        asset_type: boardAssetType ? boardAssetTypeToAssetType(boardAssetType) : undefined,
        asset_id: asset ? Number(asset.data.id) : undefined
      }
    });
    if (onClickDone) {
      onClickDone();
      return;
    }
    closeBoardsPopup();
  }, [closeBoardsPopup, onClickDone, asset]);
  return <div className="flex min-h-16 w-full items-center border-0 border-gray-100 border-t-1 border-solid px-2 pr-5 dark:border-gray-800" data-sentry-component="Footer" data-sentry-source-file="popup.tsx">
      <DialogCreateBoard data-sentry-element="DialogCreateBoard" data-sentry-source-file="popup.tsx">
        <Button className="h-7 gap-1.5 text-pink-600 text-sm hover:bg-transparent active:scale-100 dark:text-white dark:hover:bg-transparent" variant="transparent" data-sentry-element="Button" data-sentry-source-file="popup.tsx">
          <Plus className="size-5" data-sentry-element="Plus" data-sentry-source-file="popup.tsx" /> New board
        </Button>
      </DialogCreateBoard>

      <div className="flex w-full items-center justify-end">
        <Button className="font-semibold text-sm" onClick={handleOnClickDone} data-sentry-element="Button" data-sentry-source-file="popup.tsx">
          Done
        </Button>
      </div>
    </div>;
};
const BoardName = ({
  name,
  isPinned,
  className
}: {
  name: string;
  isPinned?: boolean;
  className?: string;
}) => <div className="flex w-full items-center gap-1.5" data-sentry-component="BoardName" data-sentry-source-file="popup.tsx">
    {!!isPinned && <Pin className="size-4 min-w-4" />}
    <span className={cn('line-clamp-1 w-full font-sans text-base text-black dark:text-white', isPinned ? 'font-semibold' : 'font-normal', className)}>
      {name}
    </span>
  </div>;
interface BoardRowProps {
  className?: string;
  data: GetBoardsItemResponse;
}
const BoardRow = ({
  className,
  data
}: BoardRowProps) => {
  const {
    asset,
    removeAssetFromBoard,
    addAssetToBoard,
    setBoardsState,
    assetExistsInBoard
  } = useBoardsContext();
  const boardId = data?.id;
  const boardAssetType = useMemo(() => {
    if (!asset) {
      return null;
    }
    return getBoardAssetTypeFromAssetGroup(asset);
  }, [asset]);
  const checked = useMemo(() => {
    let result = false;
    if (!(boardAssetType && boardId)) {
      return false;
    }
    const assetId = Number(asset?.data.id);
    result = assetExistsInBoard({
      assetId,
      assetType: boardAssetType,
      boardId
    });
    return result;
  }, [asset, assetExistsInBoard, boardAssetType, boardId]);
  const handleOnCheckedChange = useCallback((value: CheckboxType.CheckedState) => {
    const assetId = asset?.data.id;
    if (!(boardId && boardAssetType && assetId)) {
      return;
    }
    const params = {
      assetId: Number(assetId),
      assetType: boardAssetType,
      boardId
    };
    if (value === true) {
      addAssetToBoard(params);
      return;
    }
    if (value === false) {
      removeAssetFromBoard(params);
      return;
    }
  }, [addAssetToBoard, removeAssetFromBoard, boardAssetType, boardId, asset]);
  if (!data) {
    return null;
  }
  return <Label className={cn('mb-0 flex cursor-pointer items-start hover:has-[[aria-checked=false]]:bg-gray-50 dark:hover:has-[[aria-checked=false]]:bg-gray-900', 'hover:has-[[aria-checked=true]]:bg-gray-50 dark:hover:has-[[aria-checked=true]]:bg-gray-900', 'rounded-xs transition-colors', className)} onClick={() => {
    setBoardsState(prev => ({
      ...prev,
      isDirty: true
    }));
  }} data-sentry-element="Label" data-sentry-component="BoardRow" data-sentry-source-file="popup.tsx">
      <div className={cn('flex w-full flex-row justify-center gap-3.5 px-3 py-4 sm:py-2')}>
        <BoardName className={checked ? 'font-semibold' : 'font-normal'} isPinned={data.isPinned} name={data.name} data-sentry-element="BoardName" data-sentry-source-file="popup.tsx" />
        <div className="flex items-center">
          <Checkbox checked={checked} className={cn('h-5 w-5 hover:cursor-pointer',
        // dark mode base styles
        'dark:border-gray-500 dark:bg-black dark:data-[state=checked]:border-pink-600 dark:data-[state=checked]:bg-pink-600',
        // dark mode hover styles
        'dark:hover:bg-black',
        // light mode base styles
        'border-gray-500 bg-white data-[state=checked]:border-pink-500 data-[state=checked]:bg-pink-500 data-[state=checked]:text-white',
        // light mode hover styles
        'hover:bg-white data-[state=checked]:text-white hover:data-[state=checked]:bg-pink-500')} id={`toggle-${data.id}`} onCheckedChange={handleOnCheckedChange} data-sentry-element="Checkbox" data-sentry-source-file="popup.tsx" />
        </div>
      </div>
    </Label>;
};
export const Boards = () => {
  const {
    boards,
    boardsState
  } = useBoardsContext();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const fadeAwaySize = useMemo(() => ({
    '--fade-away-top-height': '3rem',
    '--fade-away-bottom-height': '3rem'
  }), []);
  const fadeAwayStyles = useMemo(() => {
    const {
      visibleElement
    } = boardsState;
    if (visibleElement === 'drawer') {
      return {
        ...fadeAwaySize,
        ...FADE_AWAY_BG_COLORS
      };
    }
    return {
      ...fadeAwaySize,
      '--fade-away-to-dark': 'rgb(21 26 32 / 0)',
      '--fade-away-from-dark': 'rgb(21 26 32)' // #151A20 / bg-gray-950 / black, correct in popup and popover only
    };
  }, [boardsState, fadeAwaySize]);
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTo({
        top: scrollAreaRef.current.scrollHeight
      });
    }
  }, []);
  return <ScrollArea
  // The negative margins prevent a weird visual glitch at the top and bottom of the scroll area.
  className={'-mt-[0.4px] -mb-[0.4px] h-full w-full touch-pan-y px-6 xs:px-4'} fadeAway="y" ref={scrollAreaRef} style={fadeAwayStyles as React.CSSProperties} data-sentry-element="ScrollArea" data-sentry-component="Boards" data-sentry-source-file="popup.tsx">
      <AnimatePresence initial={false} data-sentry-element="AnimatePresence" data-sentry-source-file="popup.tsx">
        <motion.ul className="flex flex-col pb-3" data-sentry-element="motion.ul" data-sentry-source-file="popup.tsx">
          <CreateBoardDrawer data-sentry-element="CreateBoardDrawer" data-sentry-source-file="popup.tsx">
            <Button className="inline-flex w-fit gap-1 pl-2.5 font-semibold text-sm sm:hidden sm:gap-2 dark:bg-gray-800" size="lg" variant="brandSecondary" data-sentry-element="Button" data-sentry-source-file="popup.tsx">
              <Plus className="size-5" data-sentry-element="Plus" data-sentry-source-file="popup.tsx" />
              New board
            </Button>
          </CreateBoardDrawer>
          {boards.map((board, index) => {
          if (!board) {
            return null;
          }
          return <motion.li key={board.id} style={{
            lineHeight: 0
          }}>
                <BoardRow className={cn(index === 0 && 'mt-2')} data={board} />
              </motion.li>;
        })}
        </motion.ul>
      </AnimatePresence>
    </ScrollArea>;
};
const BoardsPopupStyles = cva('flex h-[340px] w-[300px] flex-col overflow-hidden rounded-xl border-1 border-gray-100 border-solid bg-white shadow-md dark:border-gray-800 dark:bg-black dark:shadow-black dark:shadow-lg', {
  variants: {
    variant: {
      hidden: 'pointer-events-none',
      open: 'pointer-events-auto'
    }
  },
  defaultVariants: {
    variant: 'hidden'
  }
});
export const BoardsPopup = ({
  className
}: {
  className?: string;
}) => {
  const [timebarAnimation, setTimebarAnimation] = useState<TimebarAnimationVariant>('idle');
  const {
    boardsPopupIsOpen,
    interruptBoardsPopupAutoBehaviour,
    closeBoardsPopup
  } = useBoardsContext();
  const onMouseEnterRef = useRef<boolean>(false);
  const handleOnMouseEnter = useCallback(() => {
    onMouseEnterRef.current = true;
    interruptBoardsPopupAutoBehaviour();
    setTimebarAnimation('interrupted');
  }, [interruptBoardsPopupAutoBehaviour]);
  const handleRouteChangeComplete = useCallback(() => {
    if (boardsPopupIsOpen) {
      closeBoardsPopup();
    }
  }, [boardsPopupIsOpen, closeBoardsPopup]);
  useRouterEvents({
    type: 'routeChangeComplete',
    handler: handleRouteChangeComplete
  });
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && boardsPopupIsOpen) {
        closeBoardsPopup();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [boardsPopupIsOpen, closeBoardsPopup]);

  /**
   * On the first render, the timebar animation begins and the boards popup keeps track of it internally, so that
   * when a new popup shows before the previous has disappeared, the timebar of the new popup will start from the beginning.
   * Works in combination with the key prop passed to this component in the BoardsPopupContainer
   */
  useEffect(() => {
    setTimebarAnimation('in_progress');
    return () => {
      setTimebarAnimation('idle');
    };
  }, []);
  return <div className={cn(BoardsPopupStyles({
    variant: boardsPopupIsOpen ? 'open' : 'hidden'
  }), className)} onMouseEnter={handleOnMouseEnter} data-sentry-component="BoardsPopup" data-sentry-source-file="popup.tsx">
      <Timebar animationVariant={timebarAnimation} className="" duration={BOARDS_POPUP_CLOSE_DELAY_MS / 1000} data-sentry-element="Timebar" data-sentry-source-file="popup.tsx" />
      <Header data-sentry-element="Header" data-sentry-source-file="popup.tsx" />
      <Boards data-sentry-element="Boards" data-sentry-source-file="popup.tsx" />
      <Footer data-sentry-element="Footer" data-sentry-source-file="popup.tsx" />
    </div>;
};